import { useState, useEffect } from 'react';

interface DayContent {
  day: string;
  label: string;
  icon: string;
  videoUrl: string;
  question: string;
  questionAnswer: string;
}

const DEFAULT_DAY_CONTENTS: DayContent[] = [
  { day: 'Lundi',    label: 'Questions de culture générale', icon: 'ri-question-answer-line', videoUrl: '', question: '', questionAnswer: '' },
  { day: 'Mardi',    label: 'Vidéos TikTok',                 icon: 'ri-tiktok-line',          videoUrl: '', question: '', questionAnswer: '' },
  { day: 'Mercredi', label: 'Vidéos YouTube',                icon: 'ri-youtube-line',         videoUrl: '', question: '', questionAnswer: '' },
  { day: 'Jeudi',    label: 'Clics publicitaires',           icon: 'ri-cursor-line',          videoUrl: '', question: '', questionAnswer: '' },
  { day: 'Vendredi', label: 'Vidéos Instagram',              icon: 'ri-instagram-line',       videoUrl: '', question: '', questionAnswer: '' },
];

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];

export default function Taches() {
  const [completedTasks, setCompletedTasks]   = useState<number[]>([]);
  const [currency, setCurrency]               = useState('FCFA');
  const [dayContents, setDayContents]         = useState<DayContent[]>(DEFAULT_DAY_CONTENTS);
  const [questionAnswer, setQuestionAnswer]   = useState('');
  const [questionError, setQuestionError]     = useState('');
  const [showQuestion, setShowQuestion]       = useState(false);
  const [activeTaskIndex, setActiveTaskIndex] = useState<number | null>(null);
  const [taskReward, setTaskReward]           = useState<number>(150);

  useEffect(() => {
    // Tâches complétées
    const saved = JSON.parse(localStorage.getItem('completedTasks') || '[]');
    setCompletedTasks(saved);

    // Montant de récompense défini par l'admin
    const savedReward = localStorage.getItem('taskReward');
    if (savedReward) {
      const parsed = parseInt(savedReward, 10);
      if (!isNaN(parsed) && parsed > 0) setTaskReward(parsed);
    }

    // Devise
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const country = user.country || 'CI';
    const COUNTRY_CONFIG: Record<string, { currency: string }> = {
      'CI': { currency: 'FCFA' }, 'SN': { currency: 'FCFA' }, 'BJ': { currency: 'FCFA' },
      'ML': { currency: 'FCFA' }, 'TG': { currency: 'FCFA' }, 'BF': { currency: 'FCFA' },
      'GA': { currency: 'FCFA' }, 'CM': { currency: 'FCFA' }, 'CG': { currency: 'FCFA' },
      'CD': { currency: 'FC'   }, 'GH': { currency: 'GH₵'  },
    };
    setCurrency((COUNTRY_CONFIG[country] || COUNTRY_CONFIG['CI']).currency);

    // Contenu des jours (liens vidéo + questions)
    const savedDayContents = localStorage.getItem('adminDayContents');
    if (savedDayContents) {
      try { setDayContents(JSON.parse(savedDayContents)); } catch { /* ignore */ }
    }
  }, []);

  // Jour actuel : 1=Lundi...5=Vendredi
  const todayJS      = new Date().getDay();
  const activeDayIndex = todayJS >= 1 && todayJS <= 5 ? todayJS - 1 : 0;
  const todayContent   = dayContents[activeDayIndex];
  const isMonday       = activeDayIndex === 0;

  const handleStartTask = (dayIndex: number) => {
    if (completedTasks.includes(dayIndex)) return;
    const dc = dayContents[dayIndex];

    // Lundi = question de culture générale
    if (dayIndex === 0 && dc.question) {
      setActiveTaskIndex(dayIndex);
      setShowQuestion(true);
      setQuestionAnswer('');
      setQuestionError('');
      return;
    }

    // Autres jours : ouvrir le lien vidéo si disponible
    if (dc.videoUrl) {
      window.open(dc.videoUrl, '_blank');
    }

    // Valider la tâche immédiatement
    completeTask(dayIndex);
  };

  const handleAnswerQuestion = () => {
    if (activeTaskIndex === null) return;
    const dc = dayContents[activeTaskIndex];
    const correct = dc.questionAnswer.trim().toLowerCase();
    const given   = questionAnswer.trim().toLowerCase();

    if (!given) {
      setQuestionError('Veuillez entrer votre réponse.');
      return;
    }
    if (correct && given !== correct) {
      setQuestionError('❌ Mauvaise réponse. Réessayez !');
      return;
    }
    setShowQuestion(false);
    completeTask(activeTaskIndex);
    setActiveTaskIndex(null);
  };

  const completeTask = (dayIndex: number) => {
    const dc      = dayContents[dayIndex];
    const updated = [...completedTasks, dayIndex];
    setCompletedTasks(updated);
    localStorage.setItem('completedTasks', JSON.stringify(updated));

    // Récupérer le téléphone de l'utilisateur connecté
    let userPhone = '';
    try {
      const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
      userPhone = cu.phone || '';
    } catch { /* ignore */ }

    // Ajouter le gain dans les profits (avec phone pour isolation multi-utilisateurs)
    const profits: Array<{ amount: number; type: string; date: string; phone?: string }> =
      JSON.parse(localStorage.getItem('userProfits') || '[]');
    profits.push({
      amount: taskReward,
      type:   `Tâche — ${dc.label}`,
      date:   new Date().toLocaleDateString('fr-FR'),
      phone:  userPhone,
    });
    localStorage.setItem('userProfits', JSON.stringify(profits));

    // Mettre à jour le solde tâches de l'utilisateur
    try {
      const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
      cu.taskBalance = (cu.taskBalance || 0) + taskReward;
      localStorage.setItem('currentUser', JSON.stringify(cu));
      if (cu.phone) {
        const ud = JSON.parse(localStorage.getItem(`user_${cu.phone}`) || '{}');
        ud.taskBalance = (ud.taskBalance || 0) + taskReward;
        localStorage.setItem(`user_${cu.phone}`, JSON.stringify(ud));
      }
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-8 text-white shadow-lg">
        <h2 className="text-2xl font-bold mb-2">Tâches quotidiennes</h2>
        <p className="text-teal-100">Accomplissez la tâche du jour pour gagner {taskReward} {currency}</p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-lg border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full">
              <i className="ri-task-line text-2xl text-teal-600"></i>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-semibold">Tâches accomplies</p>
              <p className="text-2xl font-extrabold text-gray-800">{completedTasks.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-lg border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full">
              <i className="ri-money-dollar-circle-line text-2xl text-orange-600"></i>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-semibold">Gains totaux</p>
              <p className="text-2xl font-extrabold text-gray-800">
                {(completedTasks.length * taskReward).toLocaleString()} {currency}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modale question du jour */}
      {showQuestion && activeTaskIndex !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-full">
                <i className="ri-question-answer-line text-2xl text-yellow-600"></i>
              </div>
              <div>
                <p className="font-bold text-gray-800">Question du jour</p>
                <p className="text-xs text-gray-500">Répondez correctement pour valider la tâche</p>
              </div>
            </div>
            <p className="text-base font-semibold text-gray-800 mb-4 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              {dayContents[activeTaskIndex].question}
            </p>
            <input
              type="text"
              value={questionAnswer}
              onChange={e => { setQuestionAnswer(e.target.value); setQuestionError(''); }}
              placeholder="Votre réponse..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 outline-none mb-2"
              onKeyDown={e => e.key === 'Enter' && handleAnswerQuestion()}
            />
            {questionError && (
              <p className="text-xs text-red-600 mb-3 flex items-center gap-1">
                <i className="ri-error-warning-line"></i> {questionError}
              </p>
            )}
            <div className="flex gap-3 mt-2">
              <button
                onClick={() => { setShowQuestion(false); setActiveTaskIndex(null); }}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={handleAnswerQuestion}
                className="flex-1 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl text-sm cursor-pointer whitespace-nowrap"
              >
                Valider ma réponse
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tâches de la semaine */}
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-calendar-check-line text-teal-600"></i> Tâches de la semaine
        </h3>
        <p className="text-sm text-gray-600 mb-5">
          Chaque jour a sa propre tâche. Accomplissez-la pour gagner {taskReward}F !
        </p>

        {/* Onglets jours */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {DAYS.map((day, i) => (
            <div
              key={day}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold border transition ${
                i === activeDayIndex
                  ? 'bg-teal-500 text-white border-teal-500 shadow'
                  : 'bg-gray-100 text-gray-500 border-gray-200'
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Liste des tâches */}
        <div className="space-y-3">
          {DAYS.map((day, i) => {
            const dc          = dayContents[i];
            const isToday     = i === activeDayIndex;
            const isCompleted = completedTasks.includes(i);
            const hasVideo    = !!dc.videoUrl;
            const hasQuestion = isMonday && !!dc.question;

            return (
              <div
                key={i}
                className={`flex items-center gap-4 rounded-xl px-5 py-4 shadow-sm border transition ${
                  isCompleted
                    ? 'bg-green-50 border-green-200'
                    : isToday
                    ? 'bg-teal-50 border-teal-300 shadow-md'
                    : 'bg-white border-gray-200 opacity-60'
                }`}
              >
                <div className={`w-11 h-11 flex items-center justify-center rounded-full border ${
                  isCompleted
                    ? 'bg-green-100 border-green-300'
                    : isToday
                    ? 'bg-white border-teal-300'
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  {isCompleted ? (
                    <i className="ri-check-line text-2xl text-green-600"></i>
                  ) : (
                    <i className={`${dc.icon} text-2xl ${isToday ? 'text-teal-600' : 'text-gray-400'}`}></i>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-bold text-sm ${
                    isCompleted ? 'text-green-700' : isToday ? 'text-teal-800' : 'text-gray-700'
                  }`}>
                    {dc.label}
                  </p>
                  <p className={`text-xs ${
                    isCompleted ? 'text-green-600' : isToday ? 'text-teal-600' : 'text-gray-400'
                  }`}>
                    {day} — Récompense : {taskReward} {currency}
                    {isToday && hasVideo && !isCompleted && (
                      <span className="ml-2 inline-flex items-center gap-1 text-teal-500 font-semibold">
                        <i className="ri-play-circle-line"></i> Vidéo disponible
                      </span>
                    )}
                    {isToday && hasQuestion && !isCompleted && (
                      <span className="ml-2 inline-flex items-center gap-1 text-yellow-600 font-semibold">
                        <i className="ri-question-line"></i> Question disponible
                      </span>
                    )}
                  </p>
                </div>
                {isCompleted ? (
                  <span className="bg-green-500 text-white text-xs font-extrabold px-3 py-1 rounded-full whitespace-nowrap shadow">
                    ✅ Terminée
                  </span>
                ) : isToday ? (
                  <button
                    onClick={() => handleStartTask(i)}
                    className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                  >
                    <i className={hasVideo ? 'ri-play-line' : hasQuestion ? 'ri-question-line' : 'ri-check-line'}></i>
                    Commencer
                  </button>
                ) : (
                  <span className="bg-gray-200 text-gray-500 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    Bientôt
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-teal-50 border-l-4 border-teal-400 p-6 rounded-lg">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-2xl text-teal-600 mt-0.5"></i>
          <div>
            <h4 className="font-bold text-gray-800 mb-2">Comment ça marche ?</h4>
            <ul className="text-sm text-gray-700 space-y-1.5">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Chaque jour de la semaine (Lundi à Vendredi) a une tâche unique</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Accomplissez la tâche du jour pour gagner {taskReward} {currency}</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Les jours avec vidéo : regardez la vidéo puis validez la tâche</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Le lundi : répondez à la question de culture générale pour valider</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Les gains sont ajoutés automatiquement à votre solde Tâches</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
